import React from 'react';
import NewspaperIcon from './ui/NewspaperIcon';
import { buildDeliveryTooltip } from '../utils/orderFileTooltip';

/**
 * THE "delivery paperwork is attached" marker on the order LIST — one
 * definition. Order Completion (2026-08-16); design:
 * docs/superpowers/specs/2026-08-16-order-completion.md.
 *
 * Sits beside the PO / order-number title in the table
 * (`OrderTableView.js`), fed by
 * `order.has_delivery_paperwork` — a BOOLEAN attached by all three order-list
 * endpoints via ONE batched `WHERE category = 'delivery'` query per endpoint
 * (`server/routes/orders.js`, `server/routes/approvals.js`).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 DISTINCT FROM `OrderAttachmentMarker` — NOT A RESKIN OF THE SAME COUNT
 * ═══════════════════════════════════════════════════════════════════════════
 * `OrderAttachmentMarker`'s `file_count` is a bare `COUNT(*)` over EVERY
 * category — order-level, line-item, AND delivery paperwork alike, ruled
 * 2026-08-11 specifically because a list cell cannot legibly carry a category
 * distinction. This marker is the deliberate exception to that ruling for
 * exactly one category: it answers "is there DELIVERY paperwork" and a plain
 * `attachment` file must NOT light it — pinned by
 * `server/tests/api/order-completion-list-fields.test.js` §2.2, which drops
 * the server's `category = 'delivery'` filter and asserts the test reds.
 * Both markers may render on the same order at once; they are independent
 * signals, not alternates.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ♿ SOLE CARRIER — same bar `OrderAttachmentMarker` was held to
 * ═══════════════════════════════════════════════════════════════════════════
 * There is no adjacent text — the glyph IS the message — so `role="img"` +
 * `aria-label` naming the state is the only channel. Unlike the paperclip
 * this marker carries no NUMBER (it is a boolean presence flag, never a
 * count), so the label names the state alone, the same shape
 * `components/DueIndicator.js` uses for a stateless presence signal.
 *
 * 🔀 **CARRIES A NATIVE `title` SINCE 2026-08-16 — a deliberate contract
 * change, reversing the rule this docblock stated until then.** It used to
 * carry NO native `title`, for the identical reason `OrderAttachmentMarker`
 * did not: on the table layout this sits inside the order-number element,
 * which already carries its own `title` (the PO/order-number cell's
 * tooltip), and a `title` here would SHADOW it — a native `title` on a CHILD
 * wins over its parent's.
 *
 * 🔑 **The product owner's request for a filename tooltip on the newspaper
 * itself is exactly that shadowing, asked for on purpose** — see
 * `OrderAttachmentMarker`'s docblock for the full reasoning (identical here).
 * `title` lives only on this marker's own wrapper `<span>`, so the cell's own
 * title (PO/order-number) is UNCHANGED and still answers every pixel outside
 * the marker. `role="img"` + `aria-label` remain the accessible name; the
 * native `title` is an ADDITIONAL, sighted-hover-only channel, not a
 * replacement for it.
 *
 * ⚠️ **§3.2 of `OrderDeliveryPaperworkMarker.test.js` used to pin "the marker
 * itself contributes no title" and is INVERTED to match, not repaired.**
 * Built by `buildDeliveryTooltip` (`utils/orderFileTooltip.js`) from the
 * `delivery_files` list the server attaches beside `has_delivery_paperwork`.
 *
 * 📏 **SAME MEASURED SHADE AS THE PAPERCLIP, REUSED RATHER THAN RE-MEASURED**
 * — `text-gray-500`, bare, no `dark:` pair. It sits in the SAME row/cell
 * background as `OrderAttachmentMarker` (the order list's title/meta line on
 * both layouts), so `OrderAttachmentMarker.js`'s measurement — 4.83:1 light /
 * 5.76:1 dark against that background, both clearing WCAG 1.4.11's 3:1
 * non-text minimum — transfers directly. A local re-measurement would either
 * reproduce that number or, if the two markers ever sat on different
 * backgrounds, would need its own — not the case here.
 *
 * 🔴 **NOT a button, not a link.** It reports; it does not act — same
 * reasoning as `OrderAttachmentMarker`. The row's own expand gesture is the
 * way to the files.
 */
const DELIVERY_PAPERWORK_LABEL = 'Delivery paperwork attached';

/**
 * Renders nothing unless the order carries delivery paperwork.
 *
 * @param {boolean} present
 * @param {Array<string>} [files] — delivery-category filenames the native
 *   `title` is built from (2026-08-16). Optional and tolerated absent (a
 *   client ahead of its server): the marker still renders on `present` alone,
 *   with a header-only tooltip. See `buildDeliveryTooltip`.
 */
function OrderDeliveryPaperworkMarker({ present, files, className = '' }) {
  if (present !== true) return null;

  return (
    <span
      // `flex-shrink-0` is load-bearing on both layouts for the same reason
      // as `OrderAttachmentMarker`: it sits next to a `truncate`d title in a
      // flex row that can be dragged narrow, and without it the flex
      // algorithm is free to squash the glyph instead of the text.
      className={`inline-flex items-center flex-shrink-0 text-gray-500 ${className}`}
      role="img"
      aria-label={DELIVERY_PAPERWORK_LABEL}
      title={buildDeliveryTooltip(files)}
      data-testid="order-delivery-paperwork-marker"
    >
      <NewspaperIcon className="h-3.5 w-3.5" />
    </span>
  );
}

export default OrderDeliveryPaperworkMarker;
